export interface StatusPageList {
  id: number;
  name: string;
  page_type: StatusPageType;
  theme: StatusPageTheme;
  visibility_level: StatusPageVisibilityLevel;
  private_url: string;
  public_url: string;
  cname_url: string;
  subscribers_count: string;
  active_incidents_count?: number;
  active_maintenance_count?: number;
  scheduled_maintenance_count?: number;
}

export enum UptimeCalculationType {
  BY_INCIDENTS = "BY_INCIDENTS",
  BY_CHECKS = "BY_CHECKS",
}

export enum ComponentStatusValue {
  OPERATIONAL = "operational",
  MAJOR_OUTAGE = "major-outage",
  PARTIAL_OUTAGE = "partial-outage",
  DEGRADED_PERFORMANCE = "degraded-performance",
  NOTIFICATION = "notification",
  UNDER_MAINTENANCE = "under-maintenance",
  NO_CHANGE = "",
}

export interface ComponentStatus {
  value: ComponentStatusValue;
  rank: number;
  display: string;
  color: string;
  is_operational: boolean;
  is_failure: boolean;
}

export interface Metric {
  id: number;
  name: string;
  source: string;
  service: number;
  service_name: string;
  service_type: string;
  is_visible: boolean;
  // Array of [unix_timestamp_ms, value] tuples for chart display per time range
  metric_data?: {
    day: [number, number][];
    week: [number, number][];
    month: [number, number][];
  };
}

export interface Component {
  id: number;
  name: string;
  description: string;
  group?: number;
  is_group: boolean;
  // TODO: [TM: 28.08.2023] specify type
  performance_data_type: string;
  status: string;
  auto_status_down: string;
  auto_status_up: string;
  service: number;
  service_name: string;
  service_type: string;
  service_address: string;
  service_uptime_sla: number;
  service_response_time_sla: number;
  sorting_weight: number;
  just_added: boolean;
  // ISO timestamp of the newest incident activity affecting this component; null when untouched
  last_updated_at?: string | null;
  cached_response_time: number;
  // Array of [unix_timestamp_ms, response_time_seconds] tuples for sparkline chart
  response_time_data?: [string | number, number][];
  subcomponents: Component[];
  has_drill_down?: boolean;
  // Associated check id — the drill-down identifier, matching the legacy component URL
  service_id?: number | null;
}

export enum IncidentType {
  INCIDENT = "INCIDENT",
  SCHEDULED_MAINTENANCE = "SCHEDULED_MAINTENANCE",
}

export enum IncidentTime {
  ACTIVE = "ACTIVE",
  RESOLVED = "RESOLVED",
  UPCOMING = "UPCOMING",
}

export enum IncidentSortBy {
  MOST_RECENTLY_UPDATED = "MOST_RECENTLY_UPDATED",
  MOST_RECENTLY_CREATED = "MOST_RECENTLY_CREATED",
  UPCOMING_FIRST = "UPCOMING_FIRST",
  UPCOMING_LAST = "UPCOMING_LAST",
}

interface AffectedComponentComponent {
  id: number;
  name: string;
}

export interface AffectedComponent {
  id: number;
  obj_id?: number;
  name: string;
  status: string;
  status_display: string;
  description: string;
  component: AffectedComponentComponent;
}

export enum IncidentState {
  IDENTIFIED = "identified",
  INVESTIGATION = "investigating",
  MAINTENANCE = "maintenance",
  MONITORING = "monitoring",
  NOTIFICATION = "notification",
  RESOLVED = "resolved",
}

export const IncidentStateLabels: Record<IncidentState, string> = {
  [IncidentState.IDENTIFIED]: "Identified",
  [IncidentState.INVESTIGATION]: "Investigating",
  [IncidentState.MAINTENANCE]: "Scheduled Maintenance",
  [IncidentState.MONITORING]: "Monitoring",
  [IncidentState.NOTIFICATION]: "Notification",
  [IncidentState.RESOLVED]: "Resolved",
};

export interface IncidentUpdate {
  id: number;
  updated_at?: string;
  description: string;
  is_modified: boolean;
  description_html: string;
  incident_state: IncidentState;
  incident_state_display: string;
}

export interface IncidentStatusPage {
  id: number;
  name: string;
  private_url: string | null;
  public_url: string | null;
  cname_url: string | null;
}

export interface Incident {
  id: number;
  statuspage: IncidentStatusPage;
  name: string;
  duration: number;
  starts_at: string;
  ends_at: string | null;
  updates: IncidentUpdate[];
  incident_type: IncidentType;
  include_in_global_metrics: boolean;
  update_component_status: boolean;
  notify_subscribers: boolean;
  resolve_components: boolean;
  affected_components: AffectedComponent[];
  send_maintenance_start_notification: boolean;
  latest_update_incident_state: IncidentState;
  latest_update_description: string;
}

export interface SAMLSettings {
  sp_uuid: string;
  idp_entity_id: string;
  idp_sso_target_url: string;
  idp_certificate: string;
  sp_entity_id: string;
  sp_acs_url: string;
  sp_wayfless_url: string;
  use_sha1_for_signature: boolean;
  unsigned_authn_requests: boolean;
}

export interface StatusPage extends StatusPageList {
  id: number;
  slug: string;
  cname: string;
  description: string;
  default_status_date_range: number;
  default_history_date_range: number;
  max_visible_days: number;
  max_visible_component_days: number;
  allow_subscriptions: boolean;
  allow_subscriptions_email: boolean;
  allow_subscriptions_rss: boolean;
  allow_subscriptions_slack: boolean;
  allow_subscriptions_sms: boolean;
  allow_subscriptions_webhook: boolean;
  allow_notifications: boolean;
  visibility_level: StatusPageVisibilityLevel;
  timezone: string;
  time_format: StatusPageTimeFormat;
  used_timezone: string;
  show_status_tab: boolean;
  show_active_incidents: boolean;
  show_component_response_time: boolean;
  show_history_tab: boolean;
  uptime_calculation_type: UptimeCalculationType;
  allow_pdf_report: boolean;
  show_history_snake: boolean;
  show_summary_metrics: boolean;
  show_component_history: boolean;
  show_past_incidents: boolean;
  hide_empty_tabs_status: boolean;
  hide_empty_tabs_history: boolean;
  layout_preset: StatusPageLayoutPreset;
  show_component_bars: boolean;
  show_component_descriptions: boolean;
  show_overall_status: boolean;
  show_branding_footer: boolean;
  allow_search_indexing: boolean;
  allow_drill_down: boolean;
  components: Component[];
  metrics: Metric[];
  active_incidents: Incident[];
  upcoming_maintenance: Incident[];
  logo_url: string;
  favicon_url: string;
  google_analytics_code: string;
  contact_email: string;
  custom_css: string;
  custom_header_html: string;
  custom_footer_html: string;
  custom_css_inspire: string;
  custom_header_html_inspire: string;
  custom_footer_html_inspire: string;
  theme: StatusPageTheme;
  custom_header_bg_color_hex: string;
  custom_header_text_color_hex: string;
  email_logo_url: string;
  email_from: string;
  email_reply_to: string;
  company_website_url: string;
  auth_username: string;
  auth_password: string;
  samlSettings: SAMLSettings;
  external_users_count: string;
}

export interface IDPInfo {
  name: string;
  info: string;
  idp_entity_id: string;
  idp_sso_target_url: string;
  idp_certificate: string;
}

export interface StatusPageResponse {
  statusPage: StatusPage | null;
  componentStatuses: ComponentStatus[];
  testIDPs: {
    items: IDPInfo[];
  };
}

interface StatusPageItems {
  items: [StatusPageList];
  total: number;
}

export interface StatusPageList {
  statusPages: StatusPageItems;
}

export enum StatusPageType {
  PUBLIC = "PUBLIC",
  PUBLIC_SLA = "PUBLIC_SLA",
  INTERNAL = "INTERNAL",
}

export const StatusPageTypeDisplayName: Record<StatusPageType, string> = {
  [StatusPageType.PUBLIC]: "Public Status",
  [StatusPageType.PUBLIC_SLA]: "Public SLA",
  [StatusPageType.INTERNAL]: "Internal Status",
};

export enum StatusPageStatus {
  OPERATIONAL = "OPERATIONAL",
  ACTIVE_INCIDENTS = "ACTIVE_INCIDENTS",
  ACTIVE_MAINTENANCE = "ACTIVE_MAINTENANCE",
  SCHEDULED_MAINTENANCE = "SCHEDULED_MAINTENANCE",
}

export enum StatusPageVisibilityLevel {
  UPTIME_USERS = "UPTIME_USERS",
  EXTERNAL_USERS = "EXTERNAL_USERS",
  PUBLIC = "PUBLIC",
}

export enum StatusPageTheme {
  LEGACY = "LEGACY",
  INSPIRE = "INSPIRE",
  INSPIRE_PLUS = "INSPIRE_PLUS",
}

export enum StatusPageLayoutPreset {
  EXPANDED = "EXPANDED",
  COMPACT = "COMPACT",
  MINIMAL = "MINIMAL",
}

export enum StatusPageTimeFormat {
  DEFAULT = "DEFAULT",
  TWENTY_FOUR_HOUR = "TWENTY_FOUR_HOUR",
}

export const StatusPageTimeFormatDisplay: Record<string, string> = {
  [StatusPageTimeFormat.DEFAULT]: "12H",
  [StatusPageTimeFormat.TWENTY_FOUR_HOUR]: "24H",
};

export const StatusPageDisplay: Record<string, string> = {
  [StatusPageVisibilityLevel.UPTIME_USERS]: "Uptime Users",
  [StatusPageVisibilityLevel.EXTERNAL_USERS]: "Private",
  [StatusPageVisibilityLevel.PUBLIC]: "Public",
};

export const StatusPageThemeDisplay: Record<string, string> = {
  [StatusPageTheme.LEGACY]: "Uptime Legacy",
  [StatusPageTheme.INSPIRE]: "Uptime Inspire",
};

export enum SubscriptionType {
  EMAIL = "EMAIL",
  SMS = "SMS",
  SLACK = "SLACK",
  WEBHOOK = "WEBHOOK",
  RSS = "RSS",
}

export interface SubscriberSlackIntegration {
  integration_status: string;
  workspace_name: string;
  channel_name: string;
}

export interface Subscriber {
  id: number;
  type: SubscriptionType;
  target: string;
  status: string;
  created_at?: Date;
  slack_integration: SubscriberSlackIntegration;
}

export interface ExternalUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
}

export enum SubscriptionEmailDomainType {
  ALLOWED = "ALLOWED",
  BLOCKED = "BLOCKED",
}

export interface SubscriptionEmailDomain {
  id: number;
  domain: string;
  created_at: string;
}

export interface SubscriptionEmailDomainItems {
  items: SubscriptionEmailDomain[];
  total: number;
}

export interface SubscriptionEmailDomainCount {
  total: number;
}

export interface SubscriptionEmailDomainResult {
  subscriptionEmailDomains: SubscriptionEmailDomainItems;
}

export interface SubscriptionEmailDomainResultCounts {
  allowed: SubscriptionEmailDomainCount;
  blocked: SubscriptionEmailDomainCount;
}

export type IncidentCounts = Record<IncidentTime, Record<"total", number>>;

export interface BrandColor {
  hex: string;
  type: string | null;
}

export type BrandLookupOutcome = "FOUND" | "NOT_FOUND" | "UNAVAILABLE";

export interface StatusPageBrandLookup {
  outcome: BrandLookupOutcome;
  logo_base64: string | null;
  logo_filename: string | null;
  logo_content_type: string | null;
  favicon_base64: string | null;
  favicon_filename: string | null;
  favicon_content_type: string | null;
  colors: BrandColor[];
  suggested_header_bg_color_hex: string | null;
  suggested_header_text_color_hex: string | null;
}
